import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { FirebaseAuthService } from 'src/app/shared/providers/firebase-auth.service';
import { ProductAnalyticsService } from 'src/app/shared/services/product-analytics.service';
import { environment } from 'src/environments/environment';
import { RenderJob } from './render-flow.model';
import { RenderOrientation } from './render.data';

// What the feedback is about: the free preview, or the paid videos.
export type RenderFeedbackStage = 'preview' | 'videos';

// The thumbs on each of the preview's frames (Figma 28721:44956 on the display, 28753:46102 on the
// phone; owner 2026-10-20, both frames 2026-10-21). The videos' form carries none.
export type RenderFeedbackRating = 'like' | 'dislike';

export interface RenderFeedbackChip {
  // What the backend stores: the labels may change, the ids do not.
  id: string;
  label: string;
}

// The designer's chips (Figma 28753:46113, 2026-10-21), in her order. "Model scale" is a chip now:
// the thumb says which frame it is about.
export const RENDER_FEEDBACK_IMPROVE: ReadonlyArray<RenderFeedbackChip> = [
  { id: 'lighting', label: 'Lighting' },
  { id: 'white_background', label: 'White background' },
  { id: 'color_presets', label: 'Color presets' },
  { id: 'render_quality', label: 'Render quality' },
  { id: 'dynamic_camera', label: 'Dynamic camera' },
  { id: 'speed', label: 'Speed' },
  { id: 'start_frame_rotation', label: 'Start frame rotation' },
  { id: 'model_scale', label: 'Model scale' },
  { id: 'price', label: 'Price' },
];

export interface RenderFeedback {
  stage: RenderFeedbackStage;
  rating: RenderFeedbackRating | null;
  improve: string[];
  // The frame whose thumb was pressed; null from the line under the videos.
  orientation: RenderOrientation | null;
  text: string;
}

// What this device remembers of a model and stage (and frame, for the thumbs): the thumb given, or
// that the form was sent.
type Given = RenderFeedbackRating | 'sent';

const STORAGE_KEY = '3dway.render-feedback.v2';

// Feedback on a model's preview or videos. `send` is the one place a backend plugs in. Until the
// dedicated endpoint exists (contract: POST /render_feedback {model_id, stage, rating, improve,
// orientation, text, client}, user from the token — docs/render-landing-port.md, decisions 32 and
// 37) it goes through the app's own POST /feedback, which emails the team and keeps a row in
// Spanner `feedback`: a readable summary in `text`, the whole record as JSON in `debug_data` (kind
// 'render_feedback', v3: `orientation` in place of v2's `model_scale`). A like goes the same way (owner, 2026-10-20) — an email each. What was given
// is remembered on this device until the backend can say it.
@Injectable()
export class RenderFeedbackService {
  constructor(
    private readonly http: HttpClient,
    private readonly firebaseAuth: FirebaseAuthService,
    private readonly productAnalytics: ProductAnalyticsService,
  ) {}

  // The thumb given on this frame of the model's preview, if any.
  public rating(job: RenderJob, orientation: RenderOrientation): RenderFeedbackRating | null {
    const given = this.stored[RenderFeedbackService.key(job, 'preview', orientation)];
    return given === 'like' || given === 'dislike' ? given : null;
  }

  public given(job: RenderJob, stage: RenderFeedbackStage): boolean {
    return !!this.stored[RenderFeedbackService.key(job, stage)];
  }

  public async send(job: RenderJob, feedback: RenderFeedback): Promise<void> {
    const uid = await this.firebaseAuth.getUserUid();
    const token = await this.firebaseAuth.getUserToken();
    const record = {
      kind: 'render_feedback',
      v: 3,
      model_id: job.backendId || job.modelId,
      model_name: job.name,
      source: job.source.kind,
      stage: feedback.stage,
      rating: feedback.rating,
      improve: feedback.improve,
      orientation: feedback.orientation,
      text: feedback.text,
      client: {
        ua: navigator.userAgent,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        url: window.location.href,
        at: new Date().toISOString(),
      },
    };
    const body = {
      user_id: uid,
      text: RenderFeedbackService.summary(job, feedback),
      debug_data: JSON.stringify(record),
    };
    const headers = new HttpHeaders({ Accept: 'application/json', Authorization: `Bearer ${token}` });
    await lastValueFrom(this.http.post(`${environment.endpoints.webBackend}/feedback`, body, { headers }));
    // The chips' ids and the thumb, for the analytics; the text stays with the team's email.
    this.productAnalytics.track('feedback_sent', {
      modelId: job.source.kind === 'upload' ? job.backendId : undefined,
      params: {
        stage: feedback.stage,
        rating: feedback.rating || 'none',
        orientation: feedback.orientation,
        improve: feedback.improve.join(','),
        with_text: !!feedback.text.trim(),
        sample: job.source.kind === 'sample' ? job.source.sampleId : undefined,
      },
    });
    this.remember(RenderFeedbackService.key(job, feedback.stage, feedback.orientation), feedback.rating || 'sent');
  }

  // What the team's email reads: "Render feedback · preview · portrait · Disliked · Drone 03", the
  // chips, the text. Never empty: /feedback refuses an empty text.
  private static summary(job: RenderJob, feedback: RenderFeedback): string {
    const labels = (ids: string[]) => ids.map((id) => RENDER_FEEDBACK_IMPROVE.find((chip) => chip.id === id)?.label || id).join(', ');
    const rating = feedback.rating === 'like' ? 'Liked' : feedback.rating === 'dislike' ? 'Disliked' : '';
    return [
      ['Render feedback', feedback.stage, feedback.orientation, rating, job.name].filter(Boolean).join(' · '),
      feedback.improve.length ? `Could be better: ${labels(feedback.improve)}` : '',
      feedback.text.trim(),
    ].filter(Boolean).join('\n');
  }

  // The landscape thumbs keep the key they had when they were the only ones.
  private static key(job: RenderJob, stage: RenderFeedbackStage, orientation?: RenderOrientation | null): string {
    return `${job.backendId || job.modelId}:${stage}${orientation === 'portrait' ? ':portrait' : ''}`;
  }

  private get storageKey(): string {
    return `${STORAGE_KEY}:${this.firebaseAuth.currentUser?.uid || 'guest'}`;
  }

  private get stored(): Record<string, Given> {
    try {
      const value = JSON.parse(localStorage.getItem(this.storageKey) || '{}');
      return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
    } catch {
      return {};
    }
  }

  private remember(key: string, given: Given): void {
    try {
      // The newest 200: re-set, a key moves to the end.
      const entries: Array<[string, Given]> = [...Object.entries(this.stored).filter(([item]) => item !== key), [key, given]];
      const kept = entries.slice(-200).reduce<Record<string, Given>>((all, [item, value]) => ({ ...all, [item]: value }), {});
      localStorage.setItem(this.storageKey, JSON.stringify(kept));
    } catch {
      // Private mode / blocked storage: the thumbs and the line ask again on the next visit.
    }
  }
}
